import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import SessionConstants from '../../config/SessionConstants';
import APIUtils from '../../utils/APIUtils';
import FileUpload from './FileUpload';
import MetadataMolecular from './MetadataMolecular';
import MetadataBiochemical from './MetadataBiochemical';
import MetadataClinical from './MetadataClinical';
import molecularVcfMetadataFields from './MolecularVcfFields';
import biochemCsvMetadataFields from './BiochemicalCsvFields';
import clinicalCsvMetadataFields from './ClinicalCsvFields';
import StepperWorkflow from './StepperWorkflow';
import muiInputStyles from '../../uiComponents/MuiInput/MuiInputStyles';
import { Grid, FormControl, TextField, MenuItem } from '@mui/material';
import { ErrorOutlineOutlined, ReportProblemOutlined, CheckCircleOutlineOutlined } from '@mui/icons-material';
import Loading from '../Loading';
import ConfirmMolecularWorkflowTable from './ConfirmMolecularWorkflowTable';
import ConfirmBiochemicalWorkflowTable from './ConfirmBiochemicalWorkflowTable';
import PatientRace from '../../config/PatientRace';
import PatientEthnicity from '../../config/PatientEthnicity';
import './index.css';


const RunWorkflowModal = ({ modalIsOpen, closeModal }) => {
  const navigate = useNavigate();
  const classes = muiInputStyles();
  const [programId, setProgramId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [fileType, setFileType] = useState('');
  const [metadataMolecular] = useState(molecularVcfMetadataFields);
  const [metadataBiochemical] = useState(biochemCsvMetadataFields);
  const [metadataClinical] = useState(clinicalCsvMetadataFields);
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [nextAttemptedStep, setNextAttemptedStep] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isWorkflowAttemptedModalOpen, setIsWorkflowAttemptedModalOpen] = useState(false);
  const [isWorkflowAttemptedErrorModalOpen, setIsWorkflowAttemptedErrorModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState(''); 
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [selectedWorkflowHeading, setSelectedWorkflowHeading] = useState('');
  const [fileUploadTypeErrors, setFileUploadTypeErrors] = useState('');
  const [fileUploadErrors, setFileUploadErrors] = useState('');
  const [fileUploadErrorList, setFileUploadErrorList] = useState([]);
  const [patientIds, setPatientIds] = useState([]);
  const [tableDataMolecular, setTableDataMolecular] = useState([]);
  const [tableDataBiochemical, setTableDataBiochemical] = useState([]);
  const [analyteGroups, setAnalyteGroups] = useState([]);
  const [selectedValuesMolecular, setSelectedValuesMolecular] = useState({});
  const [selectedValuesBiochemical, setSelectedValuesBiochemical] = useState({});
  const [biochemDataRows, setBiochemDataRows] = useState(null);
  const [isBiochemValid, setIsBiochemValid] = useState(false);
  const [biochemValidationError, setBiochemValidationError] = useState([]);
  const [isMolecularResponseComplete, setIsMolecularResponseComplete] = useState(false);
  const [isBiochemResponseComplete, setIsBiochemResponseComplete] = useState(false);
  const [isClinicalValid, setIsClinicalValid] = useState(false);
  const [isClinicalResponseComplete, setIsClinicalResponseComplete] = useState(false);
  const [clinicalValidationErrors, setClinicalValidationErrors] = useState([]);
  const [dropzoneMessage, setDropzoneMessage] = useState('');
  const [dropzoneExtension, setDropzoneExtension] = useState('');

  const molecularWorkflow = 'Upload Molecular VCF File';
  const biochemicalWorkflow = 'Upload Biochemical CSV File';
  const clinicalWorkflow = 'Upload Clinical CSV or XLSX File';
  const ed3nConfig = __ED3N_CONFIG__;
  const FIELD_POLLING_INTERVAL = 300; // milliseconds

  // Define const formFieldKeysMolecular to hold reference form field names
  const formFieldKeysMolecular = Object.keys({
    workflowName: '',
    instrument: '',
    variantCaller: '',
    reagentKit: '',
    patientId: '',
    specimenId: '',
    patientDateOfBirth: '',
    patientRace: '',
    patientEthnicity: '',
    patientSex: '',
  });

  const formFieldKeysBiochemical = Object.keys({
    workflowName: '',
  });

  const formFieldKeysClinical = Object.keys({
    workflowName: '',
  });

  // Initialize formValues dynamically
  const [formValuesMolecular, setFormValuesMolecular] = useState(
    Object.fromEntries(formFieldKeysMolecular.map(key => [key, '']))
  );
  const [formValuesBiochemical, setFormValuesBiochemical] = useState(
    Object.fromEntries(formFieldKeysBiochemical.map(key => [key, '']))
  );
  const [formValuesClinical, setFormValuesClinical] = useState(
    Object.fromEntries(formFieldKeysClinical.map(key => [key, '']))
  );

  // Initialize errors dynamically
  const [errors, setErrors] = useState({
    selectWorkflow: "",
    fileUpload: "",
    formFieldsMolecular: Object.fromEntries(formFieldKeysMolecular.map(key => [key, ""])),
    formFieldsBiochemical: Object.fromEntries(formFieldKeysBiochemical.map(key => [key, ""])),
    formFieldsClinical: Object.fromEntries(formFieldKeysClinical.map(key => [key, ""])),
  });
  const [hasValidMetadataMolecularValues, setHasValidMetadataMolecularValues] = useState(false);
  const [hasValidMetadataBiochemicalValues, setHasValidMetadataBiochemicalValues] = useState(false);
  const [hasValidMetadataClinicalValues, setHasValidMetadataClinicalValues] = useState(false);

  const fileTypes = {
    molecularVcf: 'molecularVcf',
    biochemicalCsv: 'biochemicalCsv',
    clinicalCsv: 'clinicalCsv'
  }

  const workflowToFileType = {
    [molecularWorkflow]: fileTypes.molecularVcf,
    [biochemicalWorkflow]: fileTypes.biochemicalCsv,
    [clinicalWorkflow]: fileTypes.clinicalCsv,
  };
  
  const workflowHeadings = {
    [molecularWorkflow]: 'Select Molecular VCF File to Upload',
    [biochemicalWorkflow]: 'Select Biochemical CSV File to Upload',
    [clinicalWorkflow]: 'Select Clinical Data File to Upload',
  };

  // Friendly dropdown label; value still maps to the backend workflowName
  // ('Upload Clinical CSV or XLSX File') so all lookups keyed on clinicalWorkflow match.
  const workflowDisplayNames = {
    [clinicalWorkflow]: 'Upload Clinical Data File',
  };

  const workflowDropzoneConfig = {
    [molecularWorkflow]: {
      message: 'Drop a VCF file here',
      extension: '.VCF'
    },
    [biochemicalWorkflow]: {
      message: 'Drop a CSV file here',
      extension: '.CSV'
    },
    [clinicalWorkflow]: {
      message: 'Drop a CSV or XLSX file here',
      extension: '.CSV,.XLSX'
    }
  };

  const validateFileErrorMessages = {
    'molecularVcf': `The selected VCF file was either not a valid VCF file or contains formatting errors. 
    Please review the following errors and either fix the errors or upload a different VCF file:`,
    'biochemicalCsv': `The selected CSV file was either not a valid CSV file or contains formatting errors. 
    Please review the following errors and either fix the errors or upload a different CSV file:`,
    'clinicalCsv': (fileLabel = 'CSV') => `The selected ${fileLabel} file was either not a valid ${fileLabel} file or contains formatting errors. 
    Please review the following errors and either fix the errors or upload a different ${fileLabel} file:`,
    generic: `There was an error uploading the file.`
  }

  useEffect(() => { 
    getAvailableWorkflows();
  }, [] );

  useEffect(() => { 
    setProgramId(sessionStorage.getItem(SessionConstants.PROGRAM_ID));
    setUserEmail(sessionStorage.getItem(SessionConstants.USER_EMAIL));
  }, [] );

  // Update selectedValuesMolecular when Molecular changes
  // This initializes all of the selectedValuesMolecular (create new review) as true
  // so that if the user doesn't change any inputs, all will be submitted in
  // form as true (create new review)
  useEffect(() => {
    if (tableDataMolecular.length > 0) {
      const initialSelectedValuesMolecular = tableDataMolecular.reduce((acc, item) => {
        acc[item.chrom + '-' + item.pos] = true;  // Default every row to true 
        return acc;
      }, {});
      setSelectedValuesMolecular(initialSelectedValuesMolecular);
    }
  }, [tableDataMolecular]);

  useEffect(() => {
    // List of required fields
    const requiredFieldsMolecular = ['workflowName', 'patientId', 'specimenId', 'instrument', 'variantCaller', 'reagentKit'];
    const allValidMolecular = requiredFieldsMolecular.every(field => !!formValuesMolecular[field]);
    setHasValidMetadataMolecularValues(allValidMolecular);
  }, [formValuesMolecular]);

  useEffect(() => {
    // List of required fields
    const requiredFieldsBiochemical = ['workflowName'];
    const allValidBiochemical = requiredFieldsBiochemical.every(field => !!formValuesBiochemical[field]);
    setHasValidMetadataBiochemicalValues(allValidBiochemical);
  }, [formValuesBiochemical]);

  useEffect(() => {
    // List of required fields
    const requiredFieldsClinical = ['workflowName'];
    const allValidClinical = requiredFieldsClinical.every(field => !!formValuesClinical[field]);
    setHasValidMetadataClinicalValues(allValidClinical);
  }, [formValuesClinical]);

  const getAvailableWorkflows = async () => {
    try {
      setIsLoading(true);
      var response = await APIUtils.getAvailableWorkflows();
      response = response.filter(workflow => workflow.isVisibleInRunWorkflow === true);
      if (ed3nConfig.disableBiochemical === true) {
        response = response.filter(workflow => workflow.workflowName !== biochemicalWorkflow);
      }
      if (ed3nConfig.disableClinical === true) {
        response = response.filter(workflow => workflow.workflowName !== clinicalWorkflow);
      }

      setAvailableWorkflows(response);
    }
    catch (error) {
      console.error("Error Getting Available Workflows: " + error);
      setIsLoading(false);
    }
    finally {
      setIsLoading(false);
    }
  };

  const onClose = (response, feedback, isUploadComplete = false) => {
    setActiveStepIndex(0); // Reset to step 0
    setNextAttemptedStep(null); // Reset next-attempt tracking
    setSelectedWorkflow(""); // Reset workflow selection
    setSelectedWorkflowHeading(""); // Reset heading selection
    setIsLoading(false);
    setErrors({}); // clear errors
    setFileUploadErrors('');
    setFileUploadErrorList([]);
    setAcceptedFiles([]); // Reset selected file
    setFormValuesMolecular({}); // Reset form values
    setFormValuesBiochemical({}); // Reset form values
    setFormValuesClinical({}); // Reset form values
    setSelectedValuesMolecular({});
    setTableDataMolecular([]);
    setIsMolecularResponseComplete(false);
    setIsBiochemResponseComplete(false);
    setIsClinicalResponseComplete(false);
    setPatientIds([]);
    setBiochemDataRows(null); // Reset biochemical data row number
  
    // If the upload process completed (success or failure), trigger the corresponding modal
    if (isUploadComplete) {
      setModalMessage(feedback);
      if (feedback === "Workflow started successfully") {
        setIsWorkflowAttemptedModalOpen(true);
      } else {
        setIsWorkflowAttemptedErrorModalOpen(true);
      }
    }
  
    closeModal(response, feedback);
  };

  const handleCancel = () => {
    onClose(null, '', false); // No success/failure feedback, just close 
  };

  const handleGoToWorkflowRuns = () => {
    navigate("/workflowruns", { state: { programId } });
  };

  const validateTextField = useCallback((name, value, label, selectedWorkflow) => {
    const labelText = label || name;
    const safeValue = typeof value === 'string' ? value : '';

    if (safeValue.length === 0) {
      return `${labelText} is required.`;
    }

    if (safeValue.length > 25) {
      return 'Maximum 25 characters allowed.';
    }

    let validPattern;
    if (selectedWorkflow === molecularWorkflow) {
      // Allow spaces for workflowName field, but not for other fields like patientId/specimenId
      validPattern = name === 'workflowName' ? /^[a-zA-Z0-9-_ ]+$/ : /^[a-zA-Z0-9-_]+$/;
    } else if (selectedWorkflow === biochemicalWorkflow || selectedWorkflow === clinicalWorkflow) {
      validPattern = /^[a-zA-Z0-9-_ ]+$/; // allow spaces
    } else {
      validPattern = /^[\s\S]*$/; // fallback
    }

    if (!validPattern.test(safeValue)) {
      return `Only letters, numbers, dashes (-), underscores (_)${
        (selectedWorkflow === biochemicalWorkflow || selectedWorkflow === clinicalWorkflow || name === 'workflowName') ? ', and spaces' : ''
      } are allowed.`;
    }

    return ''; // No error
  }, []); 

  const validateFields = (activeStepIndex) => {
    let currentErrors = {
      selectWorkflow: "",
      fileUpload: "",
      formFieldsMolecular: Object.fromEntries(formFieldKeysMolecular.map(key => [key, ""])),
      formFieldsBiochemical: Object.fromEntries(formFieldKeysBiochemical.map(key => [key, ""])),
      formFieldsClinical: Object.fromEntries(formFieldKeysClinical.map(key => [key, ""])),
    };

    switch (activeStepIndex) {
      case 0:
        if (!selectedWorkflow) {
          currentErrors.selectWorkflow = 'Please select a workflow';
        }
        break;

      case 1:
        if (acceptedFiles.length === 0) {
          currentErrors.fileUpload = 'Please select a file';
        }

        if (selectedWorkflow === molecularWorkflow) {
          const trimmedFormValues = {};
          formFieldKeysMolecular.forEach(field => {
            const fieldMeta = metadataMolecular.find(f => f.name === field);
            const isTextField = fieldMeta?.type === 'text';
            trimmedFormValues[field] = isTextField
              ? formValuesMolecular[field]?.trim?.() ?? ''
              : formValuesMolecular[field] ?? '';
          });

          formFieldKeysMolecular.forEach(field => {
            const fieldMeta = metadataMolecular.find(f => f.name === field);
            const label = fieldMeta ? fieldMeta.label : field;
            const value = trimmedFormValues[field];
            const isTextField = fieldMeta?.type === 'text';

            let error = '';
            if (isTextField) {
              error = validateTextField(field, value, label, selectedWorkflow);
            } else if (fieldMeta?.required && !value) {
              error = `${label} is required`;
            }

            if (error) {
              currentErrors.formFieldsMolecular[field] = error;
            }
          });
        } else if (selectedWorkflow === biochemicalWorkflow) {
          const trimmedFormValues = {};
          formFieldKeysBiochemical.forEach(field => {
            const fieldMeta = metadataBiochemical.find(f => f.name === field);
            const isTextField = fieldMeta?.type === 'text';
            trimmedFormValues[field] = isTextField
              ? formValuesBiochemical[field]?.trim?.() ?? ''
              : formValuesBiochemical[field] ?? '';
          });

          formFieldKeysBiochemical.forEach(field => {
            const fieldMeta = metadataBiochemical.find(f => f.name === field);
            const label = fieldMeta ? fieldMeta.label : field;
            const value = trimmedFormValues[field];
            const isTextField = fieldMeta?.type === 'text';

            let error = '';
            if (isTextField) {
              error = validateTextField(field, value, label, selectedWorkflow);
            } else if (!value) {
              error = `${label} is required`;
            }

            if (error) {
              currentErrors.formFieldsBiochemical[field] = error;
            }
          });
        } else if (selectedWorkflow === clinicalWorkflow) {
          const trimmedFormValues = {};
          formFieldKeysClinical.forEach(field => {
            const fieldMeta = metadataClinical.find(f => f.name === field);
            const isTextField = fieldMeta?.type === 'text';
            trimmedFormValues[field] = isTextField
              ? formValuesClinical[field]?.trim?.() ?? ''
              : formValuesClinical[field] ?? '';
          });

          formFieldKeysClinical.forEach(field => {
            const fieldMeta = metadataClinical.find(f => f.name === field);
            const label = fieldMeta ? fieldMeta.label : field;
            const value = trimmedFormValues[field];
            const isTextField = fieldMeta?.type === 'text';

            let error = '';
            if (isTextField) {
              error = validateTextField(field, value, label, selectedWorkflow);
            } else if (!value) {
              error = `${label} is required`;
            }

            if (error) {
              currentErrors.formFieldsClinical[field] = error;
            }
          });
        }
        break;

      default:
        break;
    }

    setErrors(currentErrors);
    return Object.values(currentErrors).every(error => error === "" || (typeof error === 'object' && Object.values(error).every(e => e === "")));
  };

  const selectFile = async (file, fileType) => {
    setIsMolecularResponseComplete(false);
    setIsBiochemResponseComplete(false);
    setIsClinicalResponseComplete(false);
    setFileUploadErrorList([]);
    if (fileType === fileTypes.molecularVcf) {
      console.log('---starting attempt to validate VCF file');
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('programId', programId);
        
        const response = await APIUtils.validateVCFFile(formData);
        if (response) {
          setPatientIds(response.patientIds);
          setTableDataMolecular(response.variants);
          setIsMolecularResponseComplete(true);
          setIsLoading(false);
          return true; // Validation succeeded
        } else {
          setIsLoading(false); 
          throw new Error('No response data');
        }
      } catch (err) {
        console.error('File validation error:', err);
        const errorData = err?.response?.data;
        const errorMessage = typeof errorData === 'string'
          ? errorData
          : errorData?.title || 'An unknown error occurred.';

        setFileUploadErrors(errorMessage);
        setFileUploadTypeErrors(validateFileErrorMessages[fileType] || validateFileErrorMessages.generic);
        setIsErrorModalOpen(true);
        setIsLoading(false);
        return false; // Validation failed
      }
    }
    if (fileType === fileTypes.biochemicalCsv) {
      console.log('---starting attempt to validate CSV file');
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('programId', programId);
        
        const response = await APIUtils.validateBiochemicalCSVFile(formData);
        if (response) {
          // console.log('---response from CSV validation:', response);
          const isValid = response.isValid;
          setIsBiochemValid(isValid);
          setBiochemDataRows(response.numberOfPatient); 
          setTableDataBiochemical(response.biochemicalInstrumentationSettings);
          setAnalyteGroups(response.analyteGroupEnum);
          if (!isValid) {
            setBiochemValidationError(response.validationError); // response contains validation errors
          }
          setIsBiochemResponseComplete(true);
          setIsLoading(false);
          return true; // Validation succeeded (may still have errors)
        } else {
          setIsLoading(false); 
          throw new Error('No response data');
        }
      } catch (err) {
        console.error('File validation error:', err);
        const errorData = err?.response?.data;
        const errorMessage = typeof errorData === 'string'
          ? errorData
          : errorData?.title || 'An unknown error occurred.';

        setFileUploadErrors(errorMessage);
        setFileUploadTypeErrors(validateFileErrorMessages[fileType] || validateFileErrorMessages.generic);
        setIsErrorModalOpen(true);
        setIsLoading(false);
        return false; // Validation failed
      }
    }
    if (fileType === fileTypes.clinicalCsv) {
      console.log('---starting attempt to validate Clinical file');
      const clinicalFileLabel = file?.name?.toLowerCase().endsWith('.xlsx') ? 'XLSX' : 'CSV';
      try {
        const response = await APIUtils.validateClinicalFile(file, programId);
        if (response) {
          // console.log('---response from Clinical CSV validation:', response);
          setIsClinicalValid(response.isValid);
          setClinicalValidationErrors(response.errorMessages || []);
          setIsClinicalResponseComplete(true);
          setIsLoading(false);
          return true; // Validation succeeded
        } else {
          setIsLoading(false); 
          throw new Error('No response data');
        }
      } catch (err) {
        console.error('File validation error:', err);
        const errorData = err?.response?.data;
        // The validate endpoint returns HTTP 400 with { isValid:false, errorMessages:[...] }
        // for files that fail content validation; show those messages in the error dialog.
        if (errorData && Array.isArray(errorData.errorMessages)) {
          setFileUploadErrorList(errorData.errorMessages);
          setFileUploadErrors('');
          setFileUploadTypeErrors(validateFileErrorMessages.clinicalCsv(clinicalFileLabel));
          setIsErrorModalOpen(true);
          setIsLoading(false);
          return false;
        }
        const errorMessage = typeof errorData === 'string'
          ? errorData
          : errorData?.title || 'An unknown error occurred.';

        setFileUploadErrors(errorMessage);
        setFileUploadErrorList([]);
        setFileUploadTypeErrors(validateFileErrorMessages.clinicalCsv(clinicalFileLabel));
        setIsErrorModalOpen(true);
        setIsLoading(false);
        return false; // Validation failed
      }
    }
    return false;
  };

  const uploadFile = async (file, fileType) => {
    if (fileType === fileTypes.molecularVcf) {
      console.log('---starting attempt to upload VCF');
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', file.name);
        formData.append('programId', programId);
        formData.append('userEmail', userEmail);
        
        const mergedVariantDetails = tableDataMolecular.map((item) => ({
          ...item,
          createReview: selectedValuesMolecular[`${item.chrom}-${item.pos}`] === true, // Use boolean true/false
        }));

        // Append the merged array as a JSON string or else it will error
        formData.append('variantDetails', JSON.stringify(mergedVariantDetails));

        Object.keys(formValuesMolecular).forEach((key) => {
          const value = formValuesMolecular[key];
          // Optional fields that were never filled in or were cleared are omitted rather than sent as ''.
          if (value === null || value === undefined || String(value).trim() === '') {
            return;
          }
          if (key === 'patientRace') {
            // Convert race display value to enum key
            const raceEnumKey = Object.keys(PatientRace).find(key => PatientRace[key] === formValuesMolecular['patientRace']);
            formData.append(key, raceEnumKey || formValuesMolecular[key]);
          } else if (key === 'patientEthnicity') {
            // Convert ethnicity display value to enum key
            const ethnicityEnumKey = Object.keys(PatientEthnicity).find(key => PatientEthnicity[key] === formValuesMolecular['patientEthnicity']);
            formData.append(key, ethnicityEnumKey || formValuesMolecular[key]);
          } else {
            formData.append(key, formValuesMolecular[key]);
          }
        });
          // Log all FormData entries for review
        // console.log('---FormData entries for molecular upload:');
        // for (let [key, value] of formData.entries()) {
        //   console.log(`  ${key}: ${value}`);
        // }
        const response = await APIUtils.uploadMolecularFile(formData);
        setIsLoading(false); // Set loading to false after the response is received
          
        if (response) {
          // if success response, onClose with success response and feedback
          onClose(response, "Workflow started successfully", true);
        } else {
          console.log(JSON.stringify(response));
          // if failure response, onClose with failure response and feedback
          onClose(response, "Error starting workflow", true);
        }
      } catch (err) {
        console.error(err);
        setIsLoading(false); // Set loading to false on error
        onClose(null, "Error starting workflow", true); // Call onClose with error feedback
      }
    }
    if (fileType === fileTypes.biochemicalCsv) {
      console.log('---starting attempt to upload CSV');
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', file.name);
        formData.append('programId', programId);
        formData.append('userEmail', userEmail);
        
        Object.keys(formValuesBiochemical).forEach((key) => formData.append(key, formValuesBiochemical[key]));
        
        // Merge tableDataBiochemical and selectedValuesBiochemical for submission
        const mergedInstrumentationSettings = tableDataBiochemical.map(row => ({
          ...row,
          ...(selectedValuesBiochemical[row.AnalyteGroup] || {})
        }));
        formData.append('biochemicalInstrumentation', JSON.stringify(mergedInstrumentationSettings));

        const response = await APIUtils.uploadBiochemicalFile(formData); 
        setIsLoading(false); // Set loading to false after the response is received
          
        if (response) {
          // if success response, onClose with success response and feedback
          onClose(response, "Workflow started successfully", true);
        } else {
          // if failure response, onClose with failure response and feedback
          onClose(response, "Error starting workflow", true);
        }
      } catch (err) {
        console.error(err);
        setIsLoading(false); // Set loading to false on error
        onClose(null, "Error starting workflow", true); // Call onClose with error feedback
      }
    }
    if (fileType === fileTypes.clinicalCsv) {
      console.log('---starting attempt to upload Clinical CSV');
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', file.name);
        formData.append('programId', programId);
        formData.append('userEmail', userEmail);
        
        Object.keys(formValuesClinical).forEach((key) => formData.append(key, formValuesClinical[key]));

        const response = await APIUtils.uploadClinicalFile(formData); 
        setIsLoading(false); // Set loading to false after the response is received
          
        if (response) {
          // if success response, onClose with success response and feedback
          onClose(response, "Workflow started successfully", true);
        } else {
          // if failure response, onClose with failure response and feedback
          onClose(response, "Error starting workflow", true);
        }
      } catch (err) {
        console.error(err);
        setIsLoading(false); // Set loading to false on error
        onClose(null, "Error starting workflow", true); // Call onClose with error feedback
      }
    }
  };

  const selectFileNextClicked = async () => {
    const file = acceptedFiles[0];
    setIsLoading(true);
    
    // await file validation
    const isValid = await selectFile(file, fileType);
    if (isValid) {
      // if success, proceed to next step
      setActiveStepIndex((prevStep) => prevStep + 1);
    } else {
      // if validation fails, stay on the current step (activeStepIndex === 1)
      // Error modal will be shown automatically from fileUpload
      setIsLoading(false);
    }
  };

  const startWorkflowNextClicked = () => { // next from step 4 of 4 (activeStepIndex 3)
    const file = acceptedFiles[0];
    setIsLoading(true);
    uploadFile(file, fileType);
  };

  const handleUploadInputChange = useCallback((event) => {
    const { name, value } = event.target;

    if (selectedWorkflow === molecularWorkflow) {
      setFormValuesMolecular((prevValues) => ({ ...prevValues, [name]: value }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        formFieldsMolecular: {
          ...prevErrors.formFieldsMolecular,
          [name]: '', // clear error for updated field
        },
      }));
    } else if (selectedWorkflow === biochemicalWorkflow) {
      setFormValuesBiochemical((prevValues) => ({ ...prevValues, [name]: value }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        formFieldsBiochemical: {
          ...prevErrors.formFieldsBiochemical,
          [name]: '', // clear error for updated field
        },
      }));
    } else if (selectedWorkflow === clinicalWorkflow) {
      setFormValuesClinical((prevValues) => ({ ...prevValues, [name]: value }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        formFieldsClinical: {
          ...prevErrors.formFieldsClinical,
          [name]: '', // clear error for updated field
        },
      }));
    }
  }, [selectedWorkflow, setFormValuesMolecular, setFormValuesBiochemical, setFormValuesClinical, setErrors]);


  const handleAcceptedFiles = (files, fileRejections) => {
    const MAX_FILE_SIZE_MB = ed3nConfig.maxFileSizeMB || 1; // Default 1MB
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    
    // Check file size first
    if (files.length > 0 && files[0].size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (files[0].size / (1024 * 1024)).toFixed(2);
      setErrors((prev) => ({ 
        ...prev, 
        fileUpload: `File size (${fileSizeMB} MB) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB.` 
      }));
      setAcceptedFiles([]); // Don't accept the file
      return;
    }
    
    setAcceptedFiles(files);
    setBiochemValidationError([]);//clear biochemical validation errors on new file selection
    if (files.length === 0) {
      // react-dropzone v8 passes rejected files as plain File objects
      const hasInvalidType = fileRejections?.length > 0 &&
        fileRejections.some(f => {
          const fileName = f.name || f.path || '';
          const allowedExts = dropzoneExtension.toLowerCase().split(',').map(e => e.trim());
          return !allowedExts.some(ext => fileName.toLowerCase().endsWith(ext));
        });
      if (hasInvalidType) {
        const ext = dropzoneExtension.toLowerCase().split(',').map(e => e.trim()).join(' or ');
        setErrors((prev) => ({ ...prev, fileUpload: `File upload failed: file must have a ${ext} extension.` }));
      } else {
        setErrors((prev) => ({ ...prev, fileUpload: 'File upload failed' }));
      }
    } else {
      setErrors((prev) => ({ ...prev, fileUpload: '' })); // Clear error if file uploads successfully
    }
  };

  const handleNext = () => {
    setNextAttemptedStep(activeStepIndex);
    if (validateFields(activeStepIndex)) {
      if (activeStepIndex === 1) {
        selectFileNextClicked();
      } else if (activeStepIndex === 3) {
        startWorkflowNextClicked();
      } else {
        // Show loading spinner when navigating to step 1 with molecular workflow (needs to load dropdowns)
        if (activeStepIndex === 0 && selectedWorkflow === molecularWorkflow) {
          setIsLoading(true);
        } 
        setActiveStepIndex((prevStep) => prevStep + 1);
      }
    } else {
      setIsLoading(false); // Validation failed, stop loading spinner
    }
  };

  const handleBack = () => {
    if (activeStepIndex === 1) {
        setIsLoading(false);
    } 
    if (activeStepIndex === 2) {
      setIsLoading(true);
    }
    setActiveStepIndex((prevStep) => prevStep - 1);
    setNextAttemptedStep(null); // Reset next-attempt tracking when navigating back
    // Reset errors when going back to previous step
    setErrors({
      selectWorkflow: "",
      fileUpload: "",
      formFieldsMolecular: Object.fromEntries(formFieldKeysMolecular.map(key => [key, ""])), // Map of form field names const
      formFieldsBiochemical: Object.fromEntries(formFieldKeysBiochemical.map(key => [key, ""])), // Map of form field names const
      formFieldsClinical: Object.fromEntries(formFieldKeysClinical.map(key => [key, ""])), // Map of form field names const
    });
  };

  const handleWorkflowChange = (event) => {
    const selectedValue = event.target.value;
    const fileType = workflowToFileType[selectedValue] || '';
    const heading = workflowHeadings[selectedValue] || '';
    const dropzone = workflowDropzoneConfig[selectedValue] || {};
    setAcceptedFiles([]); // Reset accepted files when workflow changes
    setFormValuesMolecular({}); // Reset form values when workflow changes
    setFormValuesBiochemical({}); // Reset form values when workflow changes
    setFormValuesClinical({}); // Reset form values when workflow changes
    setFileType(fileType);
    setSelectedWorkflow(selectedValue);
    setSelectedWorkflowHeading(heading); // Set heading based on selection
    setDropzoneMessage(dropzone.message || '');
    setDropzoneExtension(dropzone.extension || '');
    setErrors((prev) => ({ ...prev, selectWorkflow: "" })); // clear error
  };

  const handleMolecularTableSelectionChange = (chrom, pos, value) => {
    setSelectedValuesMolecular((prev) => ({
      ...prev,
      [`${chrom}-${pos}`]: value, // This will be a boolean from the ConfirmMolecularWorkflowTable TextField input
    }));
  };

  const handleBiochemicalTableSelectionChange = (analyteGroup, values) => {
    setSelectedValuesBiochemical((prev) => ({
      ...prev,
      [analyteGroup]: {
        ...prev[analyteGroup],
        ...values
      }
    }));
  };

  const tabPanel = (dropzoneTxt, fileExt) => (
    <>
    <div className="requiredContainer">
      <span className="requiredIndicator">*</span> Indicates a required field.
    </div>
    <div className='sectionContent uploadModalSection'>
      <FileUpload 
        dropZoneMsg={dropzoneTxt} 
        accept={fileExt} 
        errorMsg={errors.fileUpload} 
        accepted={acceptedFiles} 
        acceptedFiles={handleAcceptedFiles} 
      />
      { 
        selectedWorkflow === molecularWorkflow ? 
          <MetadataMolecular 
            selectedWorkflow={selectedWorkflow}
            metadata={metadataMolecular} 
            formValues={formValuesMolecular} 
            errors={errors.formFieldsMolecular} 
            handleUploadInputChange={handleUploadInputChange} 
            onReady={() => setIsLoading(false)}
            validateTextField={validateTextField}
            hasAttemptedNext={nextAttemptedStep === activeStepIndex}
            fieldPollingInterval={FIELD_POLLING_INTERVAL}
          />

        : selectedWorkflow === biochemicalWorkflow ?
          <MetadataBiochemical 
            selectedWorkflow={selectedWorkflow}
            metadata={metadataBiochemical} 
            formValues={formValuesBiochemical} 
            errors={errors.formFieldsBiochemical} 
            handleUploadInputChange={handleUploadInputChange} 
            onReady={() => setIsLoading(false)}
            validateTextField={validateTextField}
            hasAttemptedNext={nextAttemptedStep === activeStepIndex}
            fieldPollingInterval={FIELD_POLLING_INTERVAL}
          />
        : selectedWorkflow === clinicalWorkflow ?
          <MetadataClinical 
            selectedWorkflow={selectedWorkflow}
            metadata={metadataClinical} 
            formValues={formValuesClinical} 
            errors={errors.formFieldsClinical} 
            handleUploadInputChange={handleUploadInputChange} 
            onReady={() => setIsLoading(false)}
            validateTextField={validateTextField}
            hasAttemptedNext={nextAttemptedStep === activeStepIndex}
            fieldPollingInterval={FIELD_POLLING_INTERVAL}
          />
        : null
      }
    </div>
    </>
  );

  return (
    <>
      <Loading isLoading={isLoading}/>
      <Modal
        className="modal workflowModal runWorkflowModal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel="Workflow"
        shouldCloseOnOverlayClick={false}
        isOpen={modalIsOpen}
        onRequestClose={onClose}
      >
        <div className="modalHeader">Workflow</div>
        <div className="modalContent">
          <StepperWorkflow activeStepIndex={activeStepIndex}/>
           <div className="workflowStepperContainer">
            {
              activeStepIndex === 0 ? 
                <>
                  <div className="sectionHeading center">Step 1 of 4: Select Workflow</div>
                  <div className="requiredContainer">
                    <span className="requiredIndicator">*</span> Indicates a required field.
                  </div>
                  <div className="sectionContent workflowModalSection">
                    <Grid container spacing={3} className="">
                      <Grid item xs={3}></Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth>
                          <TextField
                            size="small"
                            id="selectWorkflow"
                            className={classes.muiInputs}
                            value={selectedWorkflow}
                            onChange={handleWorkflowChange}
                            variant="outlined"
                            label="Select Workflow"
                            select
                            role="combobox" // MUI role modification for aria-required and additional required elements
                            aria-required="true"
                            aria-controls="selectWorkflow"
                            aria-expanded="false"
                            required
                            error={!!errors.selectWorkflow}
                            helperText={errors.selectWorkflow}
                            SelectProps={{ 
                              MenuProps: { 
                                disableScrollLock: true, 
                                disablePortal: true, // makes the popover render inside the normal React tree (instead of attaching it to the body)
                              }, 
                            }}
                            FormHelperTextProps={{
                              style: { color: "#DA190B" }
                            }}
                          >
                            {Array.isArray(availableWorkflows) && availableWorkflows.length > 0 ? (
                              availableWorkflows.map((workflow) => (
                                <MenuItem key={workflow.workflowId} value={workflow.workflowName}>
                                  {workflowDisplayNames[workflow.workflowName] || workflow.workflowName}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>No workflows available</MenuItem>
                            )}
                          </TextField>
                          {selectedWorkflow === molecularWorkflow && (
                            <div style={{ marginTop: 16 }}>
                              <p>
                                This workflow will upload molecular variant data to the ED3N platform.
                                This workflow uses Variant Call Format (VCF) files, and should conform
                                to the standard VCF file structure as specified by the Broad
                                Institute's <a
                                  href="https://gatk.broadinstitute.org/hc/en-us/articles/360035531692-VCF-Variant-Call-Format"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  GATK
                                </a>.
                              </p>
                              <p>
                                Instruments, reagent kits, and variant callers are captured in ED3N for
                                quality assurance purposes in compliance with CDC policy.
                              </p>
                            </div>
                          )}
                          {selectedWorkflow === biochemicalWorkflow && (
                            <div style={{ marginTop: 16 }}>
                              <p>
                                This workflow will upload biochemical test data to the ED3N platform. 
                                All data uploaded into ED3N must be in CSV format and conform with the ED3N biochemical data template. 
                              </p>
                              <a
                                href="/documents/ED3N_CSV_Biochemical_Data_Template.xlsx"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "inline-block", marginTop: 8 }}
                              >
                                Download the ED3N CSV Biochemical Data Template
                              </a>
                              <p>
                                Instruments, reagent kits, and lot numbers are captured in ED3N for quality assurance purposes in compliance with CDC policy.
                              </p>
                            </div>
                          )}
                          {selectedWorkflow === clinicalWorkflow && (
                            <div style={{ marginTop: 16 }}>
                              <p>
                                This workflow will upload clinical case data into the ED3N platform.
                                All clinical data uploaded into ED3N must use the APHL NewSTEPs file format.
                              </p>
                              <a
                                href="/documents/NewSTEPS_Clinical_File_Template.xlsx"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "block", marginTop: 8 }}
                              >
                                Download the NewSTEPs Clinical File Template
                              </a>
                              <a
                                href="/documents/NewSTEPS_Template_Schema_Explanation.pdf"
                                download
                                rel="noopener noreferrer"
                                style={{ display: "block", marginTop: 8 }}
                              >
                                Download the NewSTEPs Template Schema Explanation
                              </a>
                            </div>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}></Grid>
                    </Grid>
                  </div>
                </> 
              :
              activeStepIndex === 1 ? 
                <>
                  <div className="sectionHeading center">Step 2 of 4: {selectedWorkflowHeading}</div>
                  {tabPanel(dropzoneMessage, dropzoneExtension)}
                </> 
              :
              activeStepIndex === 2 ? 
                <>
                  {
                    isMolecularResponseComplete && selectedWorkflow === molecularWorkflow ? 
                    <>
                    <div className="sectionHeading center">Step 3 of 4: Confirm Workflow</div>
                    <div className="sectionContent workflowModalSection center">
                      Review the following file contents to ensure accuracy and select an operation for the variants where required.
                    </div>
                      <div className="sectionContent center">
                        <strong style={{fontSize: '1.2rem'}}>Patient ID{patientIds.length > 1 ? 's' : ''}:  {patientIds.length > 1 ? patientIds.join(', ') : patientIds} </strong>
                      </div>
                      <div className="sectionContent center">
                        <ConfirmMolecularWorkflowTable 
                          data={tableDataMolecular} 
                          selectedValues={selectedValuesMolecular} 
                          handleTableSelectionChange={handleMolecularTableSelectionChange} 
                        />
                      </div>
                    </>
                    : null
                  }
                  {
                    isBiochemResponseComplete && selectedWorkflow === biochemicalWorkflow ? 
                      <>
                        {
                          isBiochemValid ?
                            <>
                            <div className="sectionHeading center">Step 3 of 4: Select Instrument and Reagent Kits Used</div>
                            <div className="sectionContent center">
                              <strong style={{fontSize: '1.2rem'}}>File has passed validation. {biochemDataRows} data rows detected.</strong>
                            </div>
                            <div className="sectionContent center">
                              Please review and input Instrument, Reagent Kit, and Lot Number values for each test.
                            </div>
                            <div className="sectionContent center">
                              <ConfirmBiochemicalWorkflowTable 
                                data={tableDataBiochemical} 
                                analyteGroups={analyteGroups}
                                selectedValues={selectedValuesBiochemical} 
                                handleTableSelectionChange={handleBiochemicalTableSelectionChange} 
                              />                            
                            </div>
                            </>
                          : 
                          <div className="sectionContent center">
                            <div><strong style={{fontSize: '1.2rem'}}>Validation errors detected!</strong></div>
                            <div><strong>Please fix errors before attempting to reupload.</strong></div>
                            <ul style={{textAlign: 'left', marginTop: '1rem'}}>
                              {biochemValidationError && biochemValidationError.flatMap(errorItem => 
                                errorItem.errors
                              ).map((error, index) => (
                                <li key={`error-${error}-${index}`}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        }
                      </>
                    : null
                  }
                  {
                    isClinicalResponseComplete && selectedWorkflow === clinicalWorkflow ? 
                      <>
                        {
                          isClinicalValid ?
                            <>
                            <div className="sectionHeading center">Step 3 of 4: Validate File Contents</div>
                            <div className="sectionContent center">
                              <strong style={{fontSize: '1.2rem'}}>File has passed validation.</strong>
                            </div>
                            </>
                          : 
                          <div className="sectionContent center">
                            <div><strong style={{fontSize: '1.2rem'}}>Validation errors detected!</strong></div>
                            <div><strong>Please fix errors before attempting to reupload.</strong></div>
                            <ul style={{textAlign: 'left', marginTop: '1rem'}}>
                              {clinicalValidationErrors.map((error, index) => (
                                <li key={`error-${error}-${index}`}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        }
                      </>
                    : null
                  }
                </> 
              :
              activeStepIndex === 3 ? 
                <>
                  <div className="sectionHeading center">Step 4 of 4: Start Workflow</div>
                  <div className="sectionContent workflowModalSection startWorkflow">
                    <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <ReportProblemOutlined alt="alert" style={{color: 'var(--warning-color)', fontSize: 50, marginRight: '15px'}} />
                      Are you sure you want to start this workflow?
                    </span>
                    
                  </div>
                </> 
              :
              null
            }
          </div>
        </div>
        <div className="modalButtons workflowModalButtons">
          {/* Stepper Buttons */}
          <div>
            <button 
              className="modalButton primaryButton" 
              disabled={activeStepIndex === 0}
              onClick={() => {
                handleBack();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleBack();
                  document.querySelector('.sectionHeading')?.focus();
                }
              }}
            >
              Prev
            </button>
          </div>
          <div>
            <button className="modalButton" onClick={handleCancel}>
              Cancel
            </button>
            {activeStepIndex !== 3 ? (
              <button 
                className="modalButton primaryButton"
                disabled={activeStepIndex === 3 || biochemValidationError.length > 0} // disable Next button on step 3 if there are biochemical validation errors
                onClick={() => {
                  handleNext();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleNext();
                    document.querySelector('.sectionHeading')?.focus();
                  }
                }}
              >
                Next
              </button>
            ) : null}
            {activeStepIndex === 3 && (
              <button 
              className="modalButton primaryButton" 
              disabled={activeStepIndex !== 3 }
              onClick={() => {
                handleNext();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleNext();
                  document.querySelector('.sectionHeading')?.focus();
                }
              }}
              >
                Start Workflow
              </button>
            )}
          </div>
        </div>
      </Modal>
      {/* Error Modal */}
      <Modal
         isOpen={isErrorModalOpen}
         onRequestClose={() => setIsErrorModalOpen(false)}
         className="modal runWorkflowModal"
         overlayClassName="modalOverlay"
         aria-modal="true"
         contentLabel="Error Uploading File"
         shouldCloseOnOverlayClick={true}
       >
         <div className="modalHeader">Error</div>
         <div className="modalContent errorModalBody">
           <div className="center">
             <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
               <ErrorOutlineOutlined aria-hidden="true" fontSize='small' className="iconMarginRight" style={{color: 'var(--error-color)'}} />
               Error Uploading File
             </span>
           </div>
           <div className="center">
             {fileUploadTypeErrors}
           </div>
           <div className="center workflowErrorBox">
             {fileUploadErrorList.length > 0 ? (
               <ul style={{ textAlign: 'left', margin: 0 }}>
                 {fileUploadErrorList.map((error, index) => (
                   <li key={`file-error-${error}-${index}`}>{error}</li>
                 ))}
               </ul>
             ) : (
               <div>
                 {fileUploadErrors}
               </div>
             )}
           </div>
         </div>
         <div className="modalButtons">
           <button 
             className="modalButton"
             onClick={() => setIsErrorModalOpen(false)}
           >
             Close
           </button>
         </div>
       </Modal>
      {/* Success & Failed Modal */}
      <Modal
        isOpen={isWorkflowAttemptedModalOpen || isWorkflowAttemptedErrorModalOpen}
        onRequestClose={() => {
          setIsWorkflowAttemptedModalOpen(false);
          setIsWorkflowAttemptedErrorModalOpen(false);
        }}
        className="modal runWorkflowModal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel={isWorkflowAttemptedModalOpen ? "Success" : "Error"}
        shouldCloseOnOverlayClick={true}
      >
        <div className="modalHeader">{isWorkflowAttemptedModalOpen ? "Success" : "Error"}</div>
        <div className="modalContent center">
          <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {isWorkflowAttemptedModalOpen ? (
              <CheckCircleOutlineOutlined fontSize="small" className="iconMarginRight" style={{color: 'var(--success-color)'}} />
            ) : (
              <ErrorOutlineOutlined fontSize="small" className="iconMarginRight" style={{color: 'var(--error-color)'}} />
            )}
            {modalMessage}
          </span>
        </div>
        <div className="modalButtons">
          <button 
            className="modalButton"
            onClick={() => {
            setIsWorkflowAttemptedModalOpen(false);
            setIsWorkflowAttemptedErrorModalOpen(false);
            }}
          >
            Close
          </button>
          {isWorkflowAttemptedModalOpen && ( // only show workflow runs button if success modal
            (<button 
              className="modalButton primaryButton goToWorkflowRunsButton" 
              onClick={handleGoToWorkflowRuns}
            >
              Go to Workflow Runs
            </button>)
          )}
        </div>
      </Modal>
    </>
  );
};

export default RunWorkflowModal;
